import { useLayoutEffect, useState } from "react";

export type MotionState = "entering" | "entered" | "exiting";

type Presence = {
  present: boolean;
  state: MotionState;
};

/**
 * Keeps transient UI mounted long enough for compositor-only exit motion.
 * Entry advances on two frames so the browser commits the initial state before
 * transitioning to the resting state.
 */
export function usePresence(visible: boolean, exitDurationMs: number): Presence {
  const [presence, setPresence] = useState<Presence>(() => ({
    present: visible,
    state: visible ? "entered" : "exiting",
  }));

  useLayoutEffect(() => {
    let phaseFrame = 0;
    let settleFrame = 0;
    let exitTimer = 0;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    phaseFrame = requestAnimationFrame(() => {
      if (reduceMotion) {
        setPresence({
          present: visible,
          state: visible ? "entered" : "exiting",
        });
        return;
      }

      if (visible) {
        setPresence({ present: true, state: "entering" });
        settleFrame = requestAnimationFrame(() => {
          setPresence({ present: true, state: "entered" });
        });
        return;
      }

      setPresence((current) =>
        current.present ? { present: true, state: "exiting" } : current,
      );
      exitTimer = window.setTimeout(() => {
        setPresence({ present: false, state: "exiting" });
      }, Math.max(0, exitDurationMs));
    });

    return () => {
      cancelAnimationFrame(phaseFrame);
      cancelAnimationFrame(settleFrame);
      window.clearTimeout(exitTimer);
    };
  }, [exitDurationMs, visible]);

  return presence;
}
